import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
    ButtonsRow,
    FilterOptionsRow,
    FilterOptionsWrapper,
    FilterTitle,
    StyledButton,
} from "./Styled";
import { ColumnDiv } from "../../../styled/commonStyles";
import { Filter } from "../../../../configs/productOrder";
import { FilterType } from "../../../../configs/filterTypes";
import FilterOptionComponent from "../../FilterOption";
import { convertFilterOptionsToParam } from "../../../../utils/apiUtils";
import { DEFAULT_PAGE_SIZE } from "../../../../configs/apiConfig";

interface FilterOptionsProps {
    filterTypes: FilterType[];
    fetchItems: Function;
    setCurrentPage: Function;
    defaultFilterParams?: Record<string, string>;
}

interface StackFilterOption {
    query: string;
    fields: string[];
}

export interface FilterOptionsByType {
    [key: string]: {
        data: StackFilterOption[] | string; // string for "date" type, array for everything else
        type?: "text" | "date" | "date_range_utc" | "date_range" | "price_range" | "staff" | "category_group";
    };
}

const FilterOptions = ({
    filterTypes,
    fetchItems,
    setCurrentPage,
    defaultFilterParams,
}: FilterOptionsProps) => {
    const location = useLocation();
    const navigate = useNavigate();
    const [stackFilteredOptions, setStackFilteredOptions] =
        useState<FilterOptionsByType>({});
    const lastProcessedSearchRef = useRef<string>('');
    const isFetchingRef = useRef(false);

    useEffect(() => {
        const currentSearch = location.search || '';
        
        // Skip if we've already processed this exact URL
        if (currentSearch === lastProcessedSearchRef.current) {
            return;
        }
        
        // Skip if we're already fetching (prevents rapid successive calls)
        if (isFetchingRef.current) {
            // Update the ref so we process it once fetching completes
            lastProcessedSearchRef.current = currentSearch;
            return;
        }
        
        // Mark as fetching and processed IMMEDIATELY to prevent double calls
        // Set both refs synchronously before any async operations
        isFetchingRef.current = true;
        lastProcessedSearchRef.current = currentSearch;
        
        // Initialize filter state from URL parameters
        const params = new URLSearchParams(location.search);
        const initialFilters: FilterOptionsByType = {};
        params.forEach((value, key) => {
            // Skip non-filter parameters
            if (key === 'tab' || key === 'page' || key === 'page_size' || key === 'search' || key === 'ordering') {
                return;
            }
            const ft = filterTypes.find(f => f.field === key);
            if (ft?.type === "date") {
                // Date values stored as plain string for controlled DateFilter
                initialFilters[key] = { data: value, type: "date" };
            } else {
                if (!initialFilters[key]) initialFilters[key] = { data: [] };
                (initialFilters[key].data as StackFilterOption[]).push({ query: value, fields: [key] });
            }
        });
        // Fill defaults for date fields that are not in URL
        if (defaultFilterParams) {
            filterTypes.forEach(ft => {
                if (ft.type === "date" && !initialFilters[ft.field] && defaultFilterParams[ft.field]) {
                    initialFilters[ft.field] = { data: defaultFilterParams[ft.field], type: "date" };
                }
            });
        }
        // Detect if this is a reset (no filter params in URL)
        const hasFilters = Object.keys(initialFilters).length > 0;
        const shouldReset = !hasFilters;
        
        setStackFilteredOptions(initialFilters);
        
        // Build params including non-filter params
        const allParams: any = {
            page_size: params.get('page_size') ? parseInt(params.get('page_size')!) : DEFAULT_PAGE_SIZE,
            page: params.get('page') ? parseInt(params.get('page')!) : 1,
            ...(params.get('search') && { search: params.get('search') }),
            ...(params.get('ordering') && { ordering: params.get('ordering') }),
            ...convertFilterOptionsToParam(initialFilters),
        };
        
        fetchItems(allParams, shouldReset);
        
        // Reset fetching flag after a short delay to allow request to start
        // This prevents rapid double-clicks from causing double fetches
        setTimeout(() => {
            isFetchingRef.current = false;
        }, 100);
        
        const pageFromURL = params.get('page');
        if (pageFromURL) {
            setCurrentPage(parseInt(pageFromURL));
        } else {
            setCurrentPage(1);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.search]);

    const addStackFilterOption = (
        newOption: StackFilterOption,
        filterType: FilterType
    ) => {
        setStackFilteredOptions((prevOptions): FilterOptionsByType => {
            if (!Object.hasOwn(prevOptions, filterType.field)) {
                prevOptions[filterType.field] = {
                    data: [],
                };
            }
            let currentOptionsOfFilterType = prevOptions[filterType.field].data as StackFilterOption[];
            const optionExists = currentOptionsOfFilterType.some(
                (option: StackFilterOption) =>
                    option.query === newOption.query &&
                    JSON.stringify(option.fields) ===
                        JSON.stringify(newOption.fields)
            );
            if (optionExists) {
                return prevOptions;
            }
            currentOptionsOfFilterType = [
                ...currentOptionsOfFilterType,
                newOption,
            ];

            return {
                ...prevOptions,
                [filterType.field]: {
                    data: currentOptionsOfFilterType,
                    type: filterType.type,
                },
            };
        });
    };

    const removeStackFilterOption = (
        removeOption: StackFilterOption,
        filterType: FilterType
    ) => {
        setStackFilteredOptions((prevOptions): FilterOptionsByType => {
            const newOptions = { ...prevOptions };
    
            if (!Object.hasOwn(newOptions, filterType.field)) {
                newOptions[filterType.field] = {
                    data: [],
                };
            }
    
            let currentOptionsOfFilterType = newOptions[filterType.field].data as StackFilterOption[];
    
            currentOptionsOfFilterType = currentOptionsOfFilterType.map((option: StackFilterOption) => {
                const queries = option.query.split(",");
                const filteredQueries = queries.filter(
                    (query: string) => query !== removeOption.query
                );
                return {
                    ...option,
                    query: filteredQueries.join(","),
                };
            });
    
            currentOptionsOfFilterType = currentOptionsOfFilterType.filter(
                (option: StackFilterOption) => option.query !== ""
            );
    
            newOptions[filterType.field] = {
                data: currentOptionsOfFilterType,
                type: filterType.type,
            };

            return newOptions;
        });
    };

    const changeStackFilteredOptions = (
        filterOptionData: any,
        filterType: FilterType
    ) => {
        setStackFilteredOptions((prevOptions) => ({
            ...prevOptions,
            [filterType.field]: {
                data: filterOptionData,
                type: filterType.type,
            },
        }));
    };

    const onClickApplyFilter = () => {
        const queryParam = convertFilterOptionsToParam(stackFilteredOptions);
        
        setCurrentPage(1);
        
        // Preserve tab parameter when applying filters
        const params = new URLSearchParams(location.search);
        const currentTab = params.get('tab');
        const newParams = new URLSearchParams(queryParam);
        if (currentTab) {
            newParams.set('tab', currentTab);
        }
        
        const targetSearch = newParams.toString();
        const targetSearchWithPrefix = targetSearch ? `?${targetSearch}` : '';
        
        // Update ref BEFORE navigation to prevent useEffect from running
        lastProcessedSearchRef.current = targetSearchWithPrefix;
        
        // Navigate to update URL - useEffect will handle fetchItems
        navigate(`${location.pathname}${targetSearchWithPrefix}`);
    };

    const onClickResetFilters = () => {
        setStackFilteredOptions({});
        setCurrentPage(1);
        
        // Preserve tab parameter when resetting filters
        const params = new URLSearchParams(location.search);
        const currentTab = params.get('tab');
        
        const targetSearch = currentTab ? `tab=${currentTab}` : '';
        const targetSearchWithPrefix = targetSearch ? `?${targetSearch}` : '';
        const targetURL = currentTab 
            ? `${location.pathname}?${targetSearch}` 
            : location.pathname;
        
        // Update ref BEFORE navigation to prevent useEffect from running
        lastProcessedSearchRef.current = targetSearchWithPrefix;
        
        // Navigate to update URL - useEffect will handle fetchItems
        navigate(targetURL, { replace: true });
    };

    return (
        <FilterOptionsWrapper>
            <ColumnDiv gap="8px">
                <FilterTitle>{Filter.filterTitle}</FilterTitle>
                <FilterOptionsRow data-testid="filter-options">
                    {filterTypes.map((filterType: FilterType) => {
                        const hasFilters = Object.keys(stackFilteredOptions).length === 0;
                        return (
                            <FilterOptionComponent
                                key={filterType.field}
                                title={filterType.title}
                                filterType={filterType}
                                addStackFilterOption={addStackFilterOption}
                                removeStackFilterOption={removeStackFilterOption}
                                changeStackFilteredOptions={changeStackFilteredOptions}
                                isReset={hasFilters}
                                stackFilteredOptions={stackFilteredOptions}
                            />
                        );
                    })}
                </FilterOptionsRow>
            </ColumnDiv>

            <ButtonsRow style={{ marginTop: '12px' }}>
                <StyledButton
                    data-testid="remove-filters-button"
                    variant="outlined"
                    onClick={onClickResetFilters}
                >
                    Xoá bộ lọc
                </StyledButton>
                <StyledButton
                    data-testid="apply-filters-button"
                    variant="contained"
                    onClick={onClickApplyFilter}
                >
                    Áp dụng
                </StyledButton>
            </ButtonsRow>
        </FilterOptionsWrapper>
    );
};

export default FilterOptions;
